// src/features/task/create/logic.ts
// Pure logic behind the task creation experience. Zero React imports.
//
// The create form is *type-driven*: a TaskTypeDefinition (resolved through
// ResolveTaskContext) decides which sections exist, how dense the form starts,
// and which values are prefilled. Everything in this file answers one of those
// questions from backend data — never from a hardcoded enum table.

import type {
  CustomFieldDefinition,
  CustomFieldValue,
  InitialCustomFieldValue,
  TaskNumberingScheme,
  TaskPolicy,
  TaskType,
  TaskTypeDefinition,
} from '@core/types/task';
import { TaskTypeValues } from '@core/types/task/TaskType';
import { buildCustomFieldValuePayload, taskTypeMap, coreTaskTypeSlug } from '@/features/task/logic';
import { previewNumberingSample, durationToSeconds } from '@/features/task/panelHelpers';

// ---------------------------------------------------------------------------
// Task type definition ↔ TaskType enum
// ---------------------------------------------------------------------------

/** Stable identifier for a definition inside the form (entity id, else slug). */
export function typeDefId(def: TaskTypeDefinition | null | undefined): string {
  return def?.entity?.id || def?.slug || '';
}

/**
 * The `TaskType` enum value `CreateTask` should carry for a chosen definition.
 *
 * Built-in definitions use the `core.{enum stem}` slug convention (the inverse
 * of `coreTaskTypeSlug`). Custom definitions have no enum of their own, so they
 * fall back to `TASK_TYPE_TASK` — `CreateTaskRequest` has no `task_type_id`
 * field to carry the definition itself.
 */
export function taskTypeEnumForDefinition(def: TaskTypeDefinition | null | undefined): TaskType {
  const slug = def?.slug || '';
  const match = /^core\.(.+)$/.exec(slug);
  if (match) {
    const candidate = `TASK_TYPE_${match[1].toUpperCase()}`;
    if ((TaskTypeValues as readonly string[]).includes(candidate)) {
      return candidate as TaskType;
    }
  }
  return 'TASK_TYPE_TASK';
}

/**
 * The value for `CreateTaskRequest.task_type_id` — the registry identifier of
 * the chosen definition.
 *
 * `type` (the enum) is the coarse base kind and says nothing about the
 * registry, so a task created from a custom type would collapse to
 * `TASK_TYPE_TASK` and lose the statuses, transitions, templates and numbering
 * the type exists to carry. The backend accepts either the slug or the entity
 * id and stores the slug, so the slug is preferred.
 *
 * The `core.*` definitions are the exception: they are synthesized locally by
 * `synthesizeBaseTypeDefinitions` so the picker can still offer the base kinds
 * when a tenant has registered nothing. They exist only in this client, so
 * sending one is asking the service to honour a type it never registered — it
 * answers `task type "core.task" is not registered` and the whole create fails.
 * For those, the base enum in `type` already says everything there is to say.
 */
export function taskTypeIdForRequest(def: TaskTypeDefinition | null | undefined): string {
  const slug = def?.slug || '';
  if (isSynthesizedBaseType(def)) return '';
  return slug || def?.entity?.id || '';
}

/** True for the locally synthesized base-kind definitions (no registry entry). */
export function isSynthesizedBaseType(def: TaskTypeDefinition | null | undefined): boolean {
  return /^core\./.test(def?.slug || '') && !def?.entity?.id;
}

/** Icon path for a definition — the built-in glyph when known, else a generic one. */
const GENERIC_TYPE_ICON =
  'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2';

export function typeDefIconPath(def: TaskTypeDefinition | null | undefined): string {
  const enumValue = taskTypeEnumForDefinition(def);
  const slug = def?.slug || '';
  if (/^core\./.test(slug) && taskTypeMap[enumValue]) return taskTypeMap[enumValue].iconPath;
  return GENERIC_TYPE_ICON;
}

/** i18n key for a built-in definition's label, or null when it has its own name. */
export function typeDefLabelKey(def: TaskTypeDefinition | null | undefined): string | null {
  const slug = def?.slug || '';
  if (!/^core\./.test(slug)) return null;
  const info = taskTypeMap[taskTypeEnumForDefinition(def)];
  return info ? info.labelKey : null;
}

/**
 * Definitions to offer when the backend returned none — the built-in task types
 * as full-capability definitions, so the form is never dead on a fresh install.
 */
export function synthesizeBaseTypeDefinitions(): TaskTypeDefinition[] {
  return Object.keys(taskTypeMap).map((enumValue) => ({
    slug: coreTaskTypeSlug(enumValue),
    display_name: '',
    description: '',
    product_id: '',
    source_entity_type: '',
    default_tags: [],
    allowed_statuses: [],
    allowed_custom_statuses: [],
    status_definitions: [],
    transitions: [],
    enable_time_tracking: true,
    enable_checklists: true,
    enable_dependencies: true,
    enable_progress: true,
    enable_watchers: true,
    requires_assignee: false,
    enable_custom_fields: true,
    custom_field_ids: [],
    source_created_topic: '',
    source_updated_topic: '',
    source_deleted_topic: '',
    source_status_changed_topic: '',
    preset: 'TASK_TYPE_PRESET_STANDARD',
    is_active: true,
  }));
}

// ---------------------------------------------------------------------------
// Capabilities — which sections exist at all
// ---------------------------------------------------------------------------

export interface TaskFormCapabilities {
  timeTracking: boolean;
  checklists: boolean;
  dependencies: boolean;
  progress: boolean;
  watchers: boolean;
  customFields: boolean;
  requiresAssignee: boolean;
}

/**
 * Merge the chosen type definition with the effective policy.
 * The definition is authoritative; the policy may only switch a capability
 * *off* (it is a restriction, never a grant). With no definition loaded yet we
 * assume everything is available rather than hiding capability we cannot see.
 */
export function resolveCapabilities(
  def: TaskTypeDefinition | null | undefined,
  policy: TaskPolicy | null | undefined,
): TaskFormCapabilities {
  const from = (fromDef: boolean | undefined, fromPolicy: boolean | undefined): boolean => {
    const base = def ? fromDef !== false : true;
    return base && fromPolicy !== false;
  };
  return {
    timeTracking: from(def?.enable_time_tracking, policy?.enable_time_tracking),
    checklists: from(def?.enable_checklists, policy?.enable_checklists),
    dependencies: from(def?.enable_dependencies, policy?.enable_dependencies),
    progress: def ? def.enable_progress !== false : true,
    watchers: def ? def.enable_watchers !== false : true,
    customFields: from(def?.enable_custom_fields, policy?.enable_custom_fields),
    requiresAssignee:
      def?.requires_assignee === true || policy?.require_assignee_before_start === true,
  };
}

// ---------------------------------------------------------------------------
// Type defaults — what the definition prefills
// ---------------------------------------------------------------------------

export interface TaskTypeDefaults {
  priority?: string;
  assigneeId?: string;
  assigneeName?: string;
  estimatedHours?: string;
  tags: string[];
}

export function defaultsForType(def: TaskTypeDefinition | null | undefined): TaskTypeDefaults {
  if (!def) return { tags: [] };
  const seconds = durationToSeconds(def.default_estimated_duration as string | undefined);
  const hours = seconds > 0 ? String(Math.round((seconds / 3600) * 100) / 100) : undefined;
  return {
    priority: def.default_priority || undefined,
    assigneeId: def.default_assignee?.entity?.id || undefined,
    assigneeName: def.default_assignee?.display_name || undefined,
    estimatedHours: hours,
    tags: def.default_tags || [],
  };
}

// ---------------------------------------------------------------------------
// Numbering — the key the task will actually get
// ---------------------------------------------------------------------------

/**
 * Pick the numbering scheme that will apply, most specific first:
 * a scheme bound to both this type and this category beats one bound to either,
 * which beats a scope-wide scheme. Inactive schemes never apply.
 */
export function pickNumberingScheme(
  schemes: TaskNumberingScheme[] | undefined,
  opts: { typeId?: string; categoryId?: string },
): TaskNumberingScheme | null {
  let best: TaskNumberingScheme | null = null;
  let bestScore = -1;
  for (const scheme of schemes || []) {
    if (scheme.is_active === false) continue;
    const wantsType = !!scheme.task_type_id;
    const wantsCategory = !!scheme.category_id;
    if (wantsType && scheme.task_type_id !== opts.typeId) continue;
    if (wantsCategory && scheme.category_id !== opts.categoryId) continue;
    const score = (wantsType ? 2 : 0) + (wantsCategory ? 2 : 0) + 1;
    if (score > bestScore) {
      bestScore = score;
      best = scheme;
    }
  }
  return best;
}

/** Render the key a task created under this scheme would receive. */
export function previewTaskKey(scheme: TaskNumberingScheme | null): string {
  if (!scheme) return '';
  return previewNumberingSample(scheme);
}

// ---------------------------------------------------------------------------
// Custom fields — selection, grouping, validation, payload
// ---------------------------------------------------------------------------

export function customFieldId(def: CustomFieldDefinition): string {
  return def.entity?.id || def.slug;
}

/**
 * Narrow the custom field catalogue to what this type (then this policy) allows.
 * An explicit `custom_field_ids` on the type wins; otherwise the policy's
 * `allowed_custom_field_ids`; otherwise every definition.
 */
export function selectCustomFieldDefinitions(
  all: CustomFieldDefinition[],
  def: TaskTypeDefinition | null | undefined,
  policy: TaskPolicy | null | undefined,
): CustomFieldDefinition[] {
  const typeIds = def?.custom_field_ids || [];
  if (typeIds.length > 0) {
    const allowed = new Set(typeIds);
    return all.filter((d) => allowed.has(customFieldId(d)) || allowed.has(d.slug));
  }
  const policyIds = policy?.allowed_custom_field_ids || [];
  if (policyIds.length > 0) {
    const allowed = new Set(policyIds);
    return all.filter((d) => allowed.has(customFieldId(d)) || allowed.has(d.slug));
  }
  return all;
}

export interface CustomFieldGroup {
  /** Group name from the definition; empty string for ungrouped fields. */
  key: string;
  fields: CustomFieldDefinition[];
}

/** Group by `group`, order groups and fields by `position`. Ungrouped comes first. */
export function groupCustomFields(defs: CustomFieldDefinition[]): CustomFieldGroup[] {
  const byGroup = new Map<string, CustomFieldDefinition[]>();
  for (const def of defs) {
    const key = (def.group || '').trim();
    const bucket = byGroup.get(key);
    if (bucket) bucket.push(def);
    else byGroup.set(key, [def]);
  }
  const groups: CustomFieldGroup[] = [];
  for (const [key, fields] of byGroup) {
    groups.push({
      key,
      fields: [...fields].sort(
        (a, b) => (a.position || 0) - (b.position || 0) || a.display_name.localeCompare(b.display_name),
      ),
    });
  }
  return groups.sort((a, b) => {
    if (!a.key !== !b.key) return a.key ? 1 : -1;
    const ap = Math.min(...a.fields.map((f) => f.position || 0));
    const bp = Math.min(...b.fields.map((f) => f.position || 0));
    return ap - bp || a.key.localeCompare(b.key);
  });
}

/** A draft custom field value: single string, or slugs for MULTI_SELECT. */
export type CustomFieldDraftValue = string | string[];

export function isCustomFieldEmpty(value: CustomFieldDraftValue | undefined): boolean {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  return value.trim() === '';
}

export interface FieldError {
  key: string;
  params?: Record<string, string | number>;
}

/** Validate one custom field against `is_required` and its `validation` block. */
export function validateCustomField(
  def: CustomFieldDefinition,
  value: CustomFieldDraftValue | undefined,
): FieldError | null {
  const empty = isCustomFieldEmpty(value);
  // A checkbox is never "empty" — false is a legitimate answer.
  if (def.field_type === 'CUSTOM_FIELD_TYPE_CHECKBOX') return null;

  if (def.is_required && empty) {
    return { key: 'validation.required', params: { field: def.display_name } };
  }
  if (empty) return null;

  const validation = def.validation;
  const text = Array.isArray(value) ? value.join(',') : String(value);

  if (def.field_type === 'CUSTOM_FIELD_TYPE_NUMBER') {
    const parsed = Number(text);
    if (Number.isNaN(parsed)) {
      return { key: 'task.createForm.error.notANumber', params: { field: def.display_name } };
    }
    if (validation?.min != null && parsed < validation.min) {
      return { key: 'task.createForm.error.minValue', params: { field: def.display_name, min: validation.min } };
    }
    if (validation?.max != null && parsed > validation.max) {
      return { key: 'task.createForm.error.maxValue', params: { field: def.display_name, max: validation.max } };
    }
    return null;
  }

  if (def.field_type === 'CUSTOM_FIELD_TYPE_URL') {
    try {
      const url = new URL(text);
      if (!url.protocol) throw new Error('no protocol');
    } catch {
      return { key: 'task.createForm.error.invalidUrl', params: { field: def.display_name } };
    }
  }

  if (validation?.max_length != null && text.length > validation.max_length) {
    return {
      key: 'validation.maxLength',
      params: { field: def.display_name, max: validation.max_length },
    };
  }
  if (validation?.pattern) {
    try {
      if (!new RegExp(validation.pattern).test(text)) {
        return { key: 'task.createForm.error.patternMismatch', params: { field: def.display_name } };
      }
    } catch {
      // An unparseable server-side pattern must not block the user.
    }
  }
  return null;
}

/** Validate every visible custom field; keyed by custom field id. */
export function validateCustomFields(
  defs: CustomFieldDefinition[],
  values: Record<string, CustomFieldDraftValue>,
): Record<string, FieldError> {
  const errors: Record<string, FieldError> = {};
  for (const def of defs) {
    const error = validateCustomField(def, values[customFieldId(def)]);
    if (error) errors[customFieldId(def)] = error;
  }
  return errors;
}

/**
 * Build `initial_custom_fields` for CreateTask, typed per `field_type`.
 * MULTI_SELECT / USER / ENTITY are shaped here; every other type delegates to
 * the shared `buildCustomFieldValuePayload` used by the edit-side panel.
 */
export function buildInitialCustomFields(
  defs: CustomFieldDefinition[],
  values: Record<string, CustomFieldDraftValue>,
  labels: Record<string, string>,
): InitialCustomFieldValue[] {
  const result: InitialCustomFieldValue[] = [];
  for (const def of defs) {
    const fieldId = customFieldId(def);
    const raw = values[fieldId];
    if (isCustomFieldEmpty(raw) && def.field_type !== 'CUSTOM_FIELD_TYPE_CHECKBOX') continue;

    let value: CustomFieldValue | null = null;
    switch (def.field_type) {
      case 'CUSTOM_FIELD_TYPE_MULTI_SELECT': {
        const slugs = Array.isArray(raw) ? raw.filter(Boolean) : [];
        value = slugs.length ? ({ multi_select_value: { slugs } } as CustomFieldValue) : null;
        break;
      }
      case 'CUSTOM_FIELD_TYPE_USER': {
        const id = Array.isArray(raw) ? raw[0] : raw;
        value = id
          ? ({ user_value: { entity: { id }, display_name: labels[fieldId] || '' } } as CustomFieldValue)
          : null;
        break;
      }
      case 'CUSTOM_FIELD_TYPE_ENTITY': {
        const id = Array.isArray(raw) ? raw[0] : raw;
        value = id ? ({ entity_value: { id } } as CustomFieldValue) : null;
        break;
      }
      case 'CUSTOM_FIELD_TYPE_CHECKBOX': {
        value = { checkbox_value: raw === 'true' } as CustomFieldValue;
        break;
      }
      default:
        value = buildCustomFieldValuePayload(def, Array.isArray(raw) ? raw.join(',') : String(raw ?? ''));
    }
    if (value) result.push({ field_id: fieldId, value });
  }
  return result;
}
